import React, { useState, useEffect, useMemo } from 'react'; // Import useMemo
import { Card, CardContent } from "../ui/card";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "../ui/select";
import { Button } from "../ui/button";
import { FileDown, PieChart, BarChart2, Info, InfoIcon, Archive, CalendarIcon } from 'lucide-react';
import ExpenseChart from './ExpenseChart'; // Make sure import is correct
import { format } from 'date-fns';
import { getCurrentTaxYear, formatTaxYear, getTaxYearBoundaries } from '../../helpers/taxYearUtil';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "../ui/dialog";
import { Label } from "../ui/label";
import { Popover, PopoverContent, PopoverTrigger } from "../ui/popover";
import { Calendar } from "../ui/calendar";
import { useToast } from "../../hooks/use-toast";
import { getAuth } from 'firebase/auth';
import { expenseService } from '../../services/expenseService';
import { useTheme } from '../ui/theme-provider'; 
import { cn } from "../../lib/utils"; 

const ExpenseReports = ({ expenses, filterType, onFilterTypeChange, onExpensesUpdate }) => {
  const [periodType, setPeriodType] = useState('tax-year');
  const [periodValue, setPeriodValue] = useState('');
  const [chartType, setChartType] = useState('bar');
  const [showDetails, setShowDetails] = useState(false);
  const [availablePeriods, setAvailablePeriods] = useState({
    taxYears: [],
    calendarYears: [],
    quarters: [],
    months: []
  });
  const [showArchiveDialog, setShowArchiveDialog] = useState(false);
  const [archiveDateRange, setArchiveDateRange] = useState({
    from: new Date(new Date().getFullYear() - 3, 0, 1), // 3 years ago
    to: new Date()
  });
  const [archiveLoading, setArchiveLoading] = useState(false);
  const { theme } = useTheme(); 
  const { toast } = useToast();

  // Log incoming expenses prop - Keep this for now
  useEffect(() => {
    console.log("ExpenseReports received expenses:", expenses); 
    if (expenses.length > 0) {
      analyzeExpenseData();
    } 
    // No need to reset memoized data here, it will recalculate if expenses become empty
  }, [expenses]);

  // --- Helper Functions (Moved Up) ---
  const filterExpensesByPeriod = (expenses, periodType, periodValue) => {
    return expenses.filter(expense => {
      // Skip deleted expenses by default
      if (expense.status === 'deleted') return false;

      let expenseDate;
      if (expense.date) {
        if (typeof expense.date.toDate === 'function') {
          expenseDate = expense.date.toDate();
        } else if (expense.date._seconds) {
          expenseDate = new Date(expense.date._seconds * 1000);
        } else {
          expenseDate = new Date(expense.date);
        }
      } else if (expense.expenseDate) {
        expenseDate = new Date(expense.expenseDate);
      } else {
        return false;
      }

      if (isNaN(expenseDate.getTime())) return false;

      switch (periodType) {
        case 'tax-year': {
          const year = parseInt(periodValue, 10);
          const { start, end } = getTaxYearBoundaries(year);
          return expenseDate >= start && expenseDate <= end;
        }

        case 'calendar-year': {
          const year = parseInt(periodValue, 10);
          return expenseDate.getFullYear() === year;
        }

        case 'quarter': {
          if (!periodValue || !periodValue.includes('-Q')) {
            console.error('Invalid quarter format:', periodValue);
            return false;
          }
          
          const [year, quarter] = periodValue.split('-');
          if (!quarter || !quarter.startsWith('Q')) {
            console.error('Invalid quarter format:', quarter);
            return false;
          }
          
          const quarterNum = parseInt(quarter.substring(1), 10);
          if (isNaN(quarterNum) || quarterNum < 1 || quarterNum > 4) {
            console.error('Invalid quarter number:', quarterNum);
            return false;
          }
          
          const startMonth = (quarterNum - 1) * 3;
          const endMonth = startMonth + 2;

          const startDate = new Date(parseInt(year, 10), startMonth, 1);
          const endDate = new Date(parseInt(year, 10), endMonth + 1, 0, 23, 59, 59, 999);

          return expenseDate >= startDate && expenseDate <= endDate;
        }

        case 'month': {
          if (!periodValue || !periodValue.includes('-')) {
            console.error('Invalid month format:', periodValue);
            return false;
          }
          
          const [year, month] = periodValue.split('-');
          if (!year || !month) {
            console.error('Invalid year or month:', year, month);
            return false;
          }
          
          const startDate = new Date(parseInt(year, 10), parseInt(month, 10) - 1, 1);
          const endDate = new Date(parseInt(year, 10), parseInt(month, 10), 0, 23, 59, 59, 999);

          return expenseDate >= startDate && expenseDate <= endDate;
        }

        default:
          return true;
      }
    });
  };

  const getCategoryName = (categoryId) => {
    // Updated map with user-friendly labels
    const categories = {
      'travel': 'Travel & Mileage',
      'parking': 'Parking Fees',
      'subsistence': 'Food & Drink (Subsistence)',
      'equipment': 'Equipment & Instruments',
      'materials': 'Clinical Materials & Consumables',
      'course': 'Courses & CPD Training',
      'insurance': 'Indemnity & Insurance',
      'membership': 'GDC Fees & Memberships',
      'telephone': 'Phone & Internet',
      'software': 'Software & Subscriptions',
      'other': 'Other Business Expenses'
      // Removed older mappings if necessary
    };

    return categories[categoryId] || categoryId.charAt(0).toUpperCase() + categoryId.slice(1); // Fallback with capitalization
  };

  const createMonthlyChartData = (expensesList, limitToQuarter = false) => {
    if (expensesList.length === 0) return [];

    const monthData = {};
    const monthNames = [
      'January', 'February', 'March', 'April', 'May', 'June',
      'July', 'August', 'September', 'October', 'November', 'December'
    ];

    if (!limitToQuarter && (periodType === 'tax-year' || periodType === 'calendar-year')) {
      let year;
      if (periodType === 'calendar-year') {
        year = parseInt(periodValue, 10);
      } else {
        const taxYearStart = parseInt(periodValue, 10);

        for (let i = 3; i < 12; i++) {
          const label = `${monthNames[i]} ${taxYearStart}`;
          monthData[label] = { value: 0, sortKey: taxYearStart * 100 + i };
        }

        for (let i = 0; i < 3; i++) {
          const label = `${monthNames[i]} ${taxYearStart + 1}`;
          monthData[label] = { value: 0, sortKey: (taxYearStart + 1) * 100 + i };
        }
      }

      if (periodType === 'calendar-year') {
        for (let i = 0; i < 12; i++) {
          const label = `${monthNames[i]} ${year}`;
          monthData[label] = { value: 0, sortKey: year * 100 + i };
        }
      }
    }

    expensesList.forEach(expense => {
      let expenseDate;
      if (expense.date) {
        if (typeof expense.date.toDate === 'function') {
          expenseDate = expense.date.toDate();
        } else if (expense.date._seconds) {
          expenseDate = new Date(expense.date._seconds * 1000);
        } else {
          expenseDate = new Date(expense.date);
        }
      } else if (expense.expenseDate) {
        expenseDate = new Date(expense.expenseDate);
      } else {
        return;
      }

      if (isNaN(expenseDate.getTime())) return;

      const month = expenseDate.getMonth();
      const year = expenseDate.getFullYear();
      const label = `${monthNames[month]} ${year}`;
      const sortKey = year * 100 + month;

      if (!monthData[label]) {
        monthData[label] = { value: 0, sortKey };
      }

      monthData[label].value += parseFloat(expense.amount || expense.totalAmount || 0);
    });

    return Object.entries(monthData)
      .map(([label, data]) => ({
        label,
        value: data.value,
        sortKey: data.sortKey
      }))
      .sort((a, b) => a.sortKey - b.sortKey)
      .map(item => ({
        label: item.label,
        value: item.value
      }));
  };

  // Modify createDailyChartData to accept periodValue explicitly
  const createDailyChartData = (expensesList, currentPeriodValue) => { 
    if (expensesList.length === 0 || !currentPeriodValue) return []; // Check periodValue

    const [year, month] = currentPeriodValue.split('-').map(Number); // Use passed periodValue

    const dailyData = {};

    const daysInMonth = new Date(year, month, 0).getDate();

    // Initialize all days of the month
    for (let i = 1; i <= daysInMonth; i++) {
      const dateObj = new Date(year, month - 1, i);
      const label = format(dateObj, 'd MMM'); // Format like '5 Jun'
      dailyData[label] = { value: 0, sortKey: i };
    }

    expensesList.forEach(expense => {
      let expenseDate;
      if (expense.date) {
        if (typeof expense.date.toDate === 'function') {
          expenseDate = expense.date.toDate();
        } else if (expense.date._seconds) {
          expenseDate = new Date(expense.date._seconds * 1000);
        } else {
          expenseDate = new Date(expense.date);
        }
      } else if (expense.expenseDate) {
        expenseDate = new Date(expense.expenseDate);
      } else {
        return;
      }

      if (isNaN(expenseDate.getTime())) return;

      // Ensure the expense is within the selected month (redundant check, but safe)
      if (expenseDate.getFullYear() === year && expenseDate.getMonth() === month - 1) {
        const label = format(expenseDate, 'd MMM');

        if (dailyData[label]) {
          dailyData[label].value += parseFloat(expense.amount || expense.totalAmount || 0);
        }
        // No need for 'else' as all days are pre-initialized
      }
    });

    // Map to array, sort by day (ascending), and format
    return Object.entries(dailyData)
      .map(([label, data]) => ({
        label,
        value: data.value,
        sortKey: data.sortKey
      }))
      .sort((a, b) => a.sortKey - b.sortKey) // Corrected sorting to ascending
      .map(item => ({
        label: item.label,
        value: item.value
      }));
  };
  // --- End Helper Functions ---

  const analyzeExpenseData = () => {
    const taxYears = new Set();
    const calendarYears = new Set();
    const quarters = new Set();
    const months = new Set();

    const currentTaxYear = getCurrentTaxYear();
    const currentYear = new Date().getFullYear();
    const currentMonth = new Date().getMonth();
    const currentQuarter = Math.floor(currentMonth / 3) + 1;

    let earliestDate = new Date();
    let latestDate = new Date(0);

    expenses.forEach(expense => {
      // Skip deleted expenses when analyzing data
      if (expense.status === 'deleted') return;

      let expenseDate;

      if (expense.date) {
        if (typeof expense.date.toDate === 'function') {
          expenseDate = expense.date.toDate();
        } else if (expense.date._seconds) {
          expenseDate = new Date(expense.date._seconds * 1000);
        } else {
          expenseDate = new Date(expense.date);
        }
      } else if (expense.expenseDate) {
        expenseDate = new Date(expense.expenseDate);
      } else {
        return;
      }

      if (!isNaN(expenseDate.getTime())) {
        if (expenseDate < earliestDate) earliestDate = new Date(expenseDate);
        if (expenseDate > latestDate) latestDate = new Date(expenseDate);

        const expenseYear = expenseDate.getFullYear();
        const expenseMonth = expenseDate.getMonth();
        const expenseDay = expenseDate.getDate();

        const taxYear = (expenseMonth < 3 || (expenseMonth === 3 && expenseDay < 6))
          ? expenseYear - 1
          : expenseYear;

        taxYears.add(taxYear);
        calendarYears.add(expenseYear);

        const quarter = `${expenseYear}-Q${Math.floor(expenseMonth / 3) + 1}`;
        const month = `${expenseYear}-${expenseMonth + 1}`;

        quarters.add(quarter);
        months.add(month);
      }
    });

    const sortedTaxYears = Array.from(taxYears).sort((a, b) => b - a);
    const sortedCalendarYears = Array.from(calendarYears).sort((a, b) => b - a);

    const formattedTaxYears = sortedTaxYears.map(year => ({
      value: `${year}`,
      label: formatTaxYear(year),
      isCurrent: year === currentTaxYear
    }));

    const formattedCalendarYears = sortedCalendarYears.map(year => ({
      value: `${year}`,
      label: `${year}`,
      isCurrent: year === currentYear
    }));

    const formattedQuarters = Array.from(quarters)
      .map(quarter => {
        const [year, q] = quarter.split('-');
        return {
          value: quarter,
          label: `${q} ${year} (${getQuarterMonthRange(parseInt(q.substring(1)), parseInt(year))})`,
          sortKey: parseInt(year) * 10 + parseInt(q.substring(1)),
          isCurrent: parseInt(year) === currentYear && parseInt(q.substring(1)) === currentQuarter
        };
      })
      .sort((a, b) => b.sortKey - a.sortKey);

    const monthNames = [
      'January', 'February', 'March', 'April', 'May', 'June',
      'July', 'August', 'September', 'October', 'November', 'December'
    ];

    const formattedMonths = Array.from(months)
      .map(month => {
        const [year, m] = month.split('-');
        const monthIndex = parseInt(m) - 1;
        return {
          value: month,
          label: `${monthNames[monthIndex]} ${year}`,
          sortKey: parseInt(year) * 100 + parseInt(m),
          isCurrent: parseInt(year) === currentYear && monthIndex === currentMonth
        };
      })
      .sort((a, b) => b.sortKey - a.sortKey);

    setAvailablePeriods({
      taxYears: formattedTaxYears,
      calendarYears: formattedCalendarYears,
      quarters: formattedQuarters,
      months: formattedMonths
    });

    // Set initial periodValue only if it's not already set or if available periods change significantly
    // This prevents resetting the user's selection unnecessarily on minor expense updates
    if (!periodValue || (periodType === 'tax-year' && !formattedTaxYears.some(p => p.value === periodValue))) {
        if (formattedTaxYears.length > 0) {
            setPeriodType('tax-year');
            setPeriodValue(formattedTaxYears[0].value);
        } else if (formattedCalendarYears.length > 0) {
            setPeriodType('calendar-year');
            setPeriodValue(formattedCalendarYears[0].value);
        }
        // Add similar logic for quarters/months if needed as default
    } else if (periodType === 'calendar-year' && !formattedCalendarYears.some(p => p.value === periodValue)) {
         if (formattedCalendarYears.length > 0) {
            setPeriodType('calendar-year');
            setPeriodValue(formattedCalendarYears[0].value);
        }
        // Add fallback if calendar years also disappear
    }
    // Add checks for quarter/month if necessary
  };

  // Memoize filtered expenses
  const filteredExpenses = useMemo(() => {
    console.log("Memoizing filteredExpenses..."); // Log memoization
    if (!periodType || !periodValue || !expenses || expenses.length === 0) {
      return [];
    }
    // Now filterExpensesByPeriod is defined above
    return filterExpensesByPeriod(expenses, periodType, periodValue); 
  }, [expenses, periodType, periodValue]); // Dependencies for filtering

  // Memoize category data
  const categoryData = useMemo(() => {
    console.log("Memoizing categoryData..."); // Log memoization
    const categories = {};
    filteredExpenses.forEach(expense => {
      const category = expense.category || 'other';
      if (!categories[category]) {
        categories[category] = 0;
      }
      categories[category] += parseFloat(expense.totalAmount || expense.amount || 0); 
    });

    return Object.keys(categories)
      .sort((a, b) => categories[b] - categories[a])
      .map(category => ({
        // Now getCategoryName is defined above
        label: getCategoryName(category), 
        value: categories[category]
      }));
  }, [filteredExpenses]); // Depends only on filteredExpenses

  // Memoize time-based chart data
  const chartData = useMemo(() => {
    console.log("Memoizing chartData (time)..."); // Log memoization
    if (periodType === 'tax-year' || periodType === 'calendar-year') {
      // Now createMonthlyChartData is defined above
      return createMonthlyChartData(filteredExpenses); 
    } else if (periodType === 'quarter') {
      // Now createMonthlyChartData is defined above
      return createMonthlyChartData(filteredExpenses, true); 
    } else if (periodType === 'month') {
      // Now createDailyChartData is defined above
      return createDailyChartData(filteredExpenses, periodValue); 
    }
    return []; // Default empty array
  }, [filteredExpenses, periodType, periodValue]); // Depends on filtered data and period type/value

  const generateCSVReport = () => {
    const data = chartType === 'bar' ? chartData : categoryData;

    if (!data || data.length === 0) return;

    const headers = ['Period', 'Amount'].join(',');
    const rows = data.map(item => [
      item.label,
      item.value.toFixed(2)
    ].join(','));

    const csvContent = [headers, ...rows].join('\n');
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);

    const link = document.createElement('a');
    const reportType = chartType === 'bar' ? periodType : 'category';
    link.setAttribute('href', url);
    link.setAttribute('download', `expense_report_${reportType}_${format(new Date(), 'yyyyMMdd')}.csv`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const fetchArchiveData = async () => {
    if (!archiveDateRange.from || !archiveDateRange.to) {
      toast({
        title: "Invalid Date Range",
        description: "Please select both start and end dates",
        variant: "destructive",
      });
      return;
    }
    
    setArchiveLoading(true);
    try {
      const auth = getAuth();
      if (!auth.currentUser) return;
      
      const archivedExpenses = await expenseService.getExpensesByDateRange(
        auth.currentUser.uid,
        archiveDateRange.from,
        archiveDateRange.to
      );
      
      setShowArchiveDialog(false);
      
      if (archivedExpenses.length === 0) {
        toast({
          title: "No Data Found",
          description: "No expenses found for the selected date range",
          variant: "default",
        });
        return;
      }
      
      // Update expenses via the callback
      if (typeof onExpensesUpdate === 'function') {
        onExpensesUpdate(archivedExpenses);
      }
      
      // Re-analyze expense data to update period options based on new data
      analyzeExpenseData();
      
      toast({
        title: "Archive Data Loaded",
        description: `Loaded ${archivedExpenses.length} additional expenses for reporting`,
        variant: "success",
      });
    } catch (error) {
      console.error('Error fetching archive data:', error);
      toast({
        title: "Error",
        description: "Failed to fetch archived expenses. Please try again.",
        variant: "destructive",
      });
    } finally {
      setArchiveLoading(false);
    }
  };

  // Calculate total based on the currently displayed chart data
  const dataForChart = chartType === 'bar' ? chartData : categoryData;
  const totalExpenses = useMemo(() => {
      console.log("Memoizing totalExpenses..."); // Log memoization
      return dataForChart.reduce((sum, item) => sum + item.value, 0);
  }, [dataForChart]); // Recalculate only when dataForChart changes

  const getPeriodDisplayLabel = () => {
    switch (periodType) {
      case 'tax-year': {
        const year = parseInt(periodValue, 10);
        return `Tax Year ${year}-${year + 1}`;
      }
      case 'calendar-year':
        return `Calendar Year ${periodValue}`;
      case 'quarter': {
        const [year, quarter] = periodValue.split('-');
        return `${quarter} ${year}`;
      }
      case 'month': {
        const [year, month] = periodValue.split('-');
        const monthName = new Date(parseInt(year), parseInt(month) - 1, 1)
          .toLocaleString('default', { month: 'long' });
        return `${monthName} ${year}`;
      }
      default:
        return 'Select a Period';
    }
  };

  const getQuarterMonthRange = (quarter, year) => {
    const ranges = {
      1: 'Jan-Mar',
      2: 'Apr-Jun',
      3: 'Jul-Sep',
      4: 'Oct-Dec'
    };
    return ranges[quarter];
  };

  // Log data being passed to chart - Keep this
  console.log(`Data being passed to ExpenseChart (type: ${chartType}):`, dataForChart); 

  return (
    <div className="space-y-4">
      {/* Card: Theme-aware */}
      <Card className={cn(theme === 'light' ? 'bg-white' : 'bg-[#2A2A3E]', 'border-border')}>
        <CardContent className="p-4">
          <div className="flex justify-between items-center mb-4">
            {/* Title: Theme-aware */}
            <h2 className={cn("text-xl font-semibold", theme === 'light' ? 'text-black' : 'text-purple-300')}>Expense Reports</h2>
            <Button
              variant="outline"
              onClick={generateCSVReport}
              // Export Button: Consistent green
              className="bg-green-600 hover:bg-green-700 text-white h-8 sm:h-9 text-xs sm:text-sm px-2 sm:px-4"
              disabled={(chartType === 'bar' ? chartData : categoryData).length === 0}
              size="sm"
            >
              <FileDown size={16} className="mr-1 sm:mr-2" />
              <span className="whitespace-nowrap">Export</span>
            </Button>
          </div>
          
          {/* Info Banner: Theme-aware */}
          <div className={cn(
            "border rounded-md p-2 mb-3 text-xs flex items-center",
            theme === 'light' 
              ? 'bg-blue-50 border-blue-200 text-blue-800' 
              : 'bg-purple-950/20 border-purple-500/20 text-purple-200'
          )}>
            {/* Icon: Theme-aware */}
            <InfoIcon size={14} className={cn(
              "mr-1.5 flex-shrink-0",
              theme === 'light' ? 'text-blue-600' : 'text-purple-300'
            )} />
            <span>Showing data from last 24 months.</span>
            {/* Link: Theme-aware */}
            <button 
              onClick={() => setShowArchiveDialog(true)}
              className={cn(
                "underline ml-auto flex items-center",
                theme === 'light' 
                  ? 'text-blue-700 hover:text-blue-900' 
                  : 'text-purple-300 hover:text-purple-100'
              )}
            >
              <Archive size={12} className="mr-1" />
              <span>Access older records</span>
            </button>
          </div>

          <div className="flex flex-wrap gap-3 mb-6">
            <div className="flex flex-wrap sm:flex-nowrap gap-2 sm:gap-3 w-full sm:w-auto">
              {/* Period Type Select */}
              <div className="w-full sm:w-auto min-w-[140px]">
                <Select value={periodType} onValueChange={setPeriodType}>
                  {/* Trigger: Theme-aware */}
                  <SelectTrigger className={cn(
                    "text-sm h-9",
                    theme === 'light' 
                      ? 'bg-white border-gray-300 text-black' 
                      : 'bg-[#1F1F2E] border-purple-500/30 text-white'
                  )}>
                    <SelectValue placeholder="Period Type" />
                  </SelectTrigger>
                  {/* Content: Theme-aware */}
                  <SelectContent className={cn(
                    theme === 'light' ? 'bg-white border-gray-300' : 'bg-[#2A2A3E] border-purple-500/30'
                  )}>
                    {/* Items: Theme-aware */}
                    <SelectItem value="tax-year" className={cn(theme === 'light' ? 'text-black hover:bg-gray-100' : 'text-white hover:bg-purple-500/20')}>Tax Year</SelectItem>
                    <SelectItem value="calendar-year" className={cn(theme === 'light' ? 'text-black hover:bg-gray-100' : 'text-white hover:bg-purple-500/20')}>Calendar Year</SelectItem>
                    <SelectItem value="quarter" className={cn(theme === 'light' ? 'text-black hover:bg-gray-100' : 'text-white hover:bg-purple-500/20')}>Quarter</SelectItem>
                    <SelectItem value="month" className={cn(theme === 'light' ? 'text-black hover:bg-gray-100' : 'text-white hover:bg-purple-500/20')}>Month</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              {/* Period Value Select */}
              <div className="w-full sm:w-auto min-w-[180px]">
                <Select 
                  value={periodValue} 
                  onValueChange={setPeriodValue}
                  // ... (disabled logic remains the same) ...
                  disabled={
                    (periodType === 'tax-year' && availablePeriods.taxYears.length === 0) ||
                    (periodType === 'calendar-year' && availablePeriods.calendarYears.length === 0) ||
                    (periodType === 'quarter' && availablePeriods.quarters.length === 0) ||
                    (periodType === 'month' && availablePeriods.months.length === 0)
                  }
                >
                  {/* Trigger: Theme-aware */}
                  <SelectTrigger className={cn(
                    "text-sm h-9",
                    theme === 'light' 
                      ? 'bg-white border-gray-300 text-black' 
                      : 'bg-[#1F1F2E] border-purple-500/30 text-white'
                  )}>
                    <SelectValue placeholder={`Select ${periodType === 'tax-year' ? 'Tax Year' : periodType === 'calendar-year' ? 'Year' : periodType}`} />
                  </SelectTrigger>
                  {/* Content: Theme-aware */}
                  <SelectContent className={cn(
                    "max-h-[300px]",
                    theme === 'light' ? 'bg-white border-gray-300' : 'bg-[#2A2A3E] border-purple-500/30'
                  )}>
                    {/* Items: Theme-aware */}
                    {periodType === 'tax-year' && 
                      availablePeriods.taxYears.map(item => (
                        <SelectItem 
                          key={item.value} 
                          value={item.value}
                          className={cn(item.isCurrent ? 'font-bold' : '', theme === 'light' ? 'text-black hover:bg-gray-100' : 'text-white hover:bg-purple-500/20')}
                        >
                          {item.label} {item.isCurrent && '(Current)'}
                        </SelectItem>
                      ))
                    }
                    {periodType === 'calendar-year' && 
                      availablePeriods.calendarYears.map(item => (
                        <SelectItem 
                          key={item.value} 
                          value={item.value}
                          className={cn(item.isCurrent ? 'font-bold' : '', theme === 'light' ? 'text-black hover:bg-gray-100' : 'text-white hover:bg-purple-500/20')}
                        >
                          {item.label} {item.isCurrent && '(Current)'}
                        </SelectItem>
                      ))
                    }
                    {periodType === 'quarter' && 
                      availablePeriods.quarters.map(item => (
                        <SelectItem 
                          key={item.value} 
                          value={item.value}
                          className={cn(item.isCurrent ? 'font-bold' : '', theme === 'light' ? 'text-black hover:bg-gray-100' : 'text-white hover:bg-purple-500/20')}
                        >
                          {item.label} {item.isCurrent && '(Current)'}
                        </SelectItem>
                      ))
                    }
                    {periodType === 'month' && 
                      availablePeriods.months.map(item => (
                        <SelectItem 
                          key={item.value} 
                          value={item.value}
                          className={cn(item.isCurrent ? 'font-bold' : '', theme === 'light' ? 'text-black hover:bg-gray-100' : 'text-white hover:bg-purple-500/20')}
                        >
                          {item.label} {item.isCurrent && '(Current)'}
                        </SelectItem>
                      ))
                    }
                  </SelectContent>
                </Select>
              </div>
            </div>

            {/* Chart Type Toggle Buttons: Theme-aware */}
            <div className="flex items-center gap-2 ml-auto">
              <Button
                variant={chartType === 'bar' ? 'default' : 'outline'}
                onClick={() => setChartType('bar')}
                className={cn(
                  "size-sm",
                  chartType === 'bar'
                    ? 'bg-purple-600 text-white hover:bg-purple-700' // Active state consistent purple
                    : theme === 'light'
                      ? 'bg-white border-gray-300 text-black hover:bg-gray-100' // Inactive light
                      : 'bg-[#1F1F2E] border-purple-300/50 text-white hover:bg-purple-900/30' // Inactive dark
                )}
                size="sm"
              >
                <BarChart2 size={16} className="mr-1" />
                Time
              </Button>
              <Button
                variant={chartType === 'pie' ? 'default' : 'outline'}
                onClick={() => setChartType('pie')}
                className={cn(
                  "size-sm",
                  chartType === 'pie'
                    ? 'bg-purple-600 text-white hover:bg-purple-700' // Active state consistent purple
                    : theme === 'light'
                      ? 'bg-white border-gray-300 text-black hover:bg-gray-100' // Inactive light
                      : 'bg-[#1F1F2E] border-purple-300/50 text-white hover:bg-purple-900/30' // Inactive dark
                )}
                size="sm"
              >
                <PieChart size={16} className="mr-1" />
                Category
              </Button>
            </div>
          </div>

          {/* Chart Area */}
          {/* Check if expenses exist, but chart uses test data */}
          {expenses && expenses.length > 0 ? ( 
            <>
              {/* Summary Box: Theme-aware */}
              <div className={cn(
                "mb-4 p-3 sm:p-4 rounded-lg",
                theme === 'light' ? 'bg-gray-100' : 'bg-[#1F1F2E]'
              )}>
                <div className="flex flex-wrap justify-between items-center gap-1">
                  <div className="flex items-center">
                    {/* Period Label: Theme-aware */}
                    <h3 className={cn(
                      "text-sm sm:text-base font-medium",
                      theme === 'light' ? 'text-gray-700' : 'text-purple-300'
                    )}>
                      {getPeriodDisplayLabel()}
                    </h3>
                    {/* Info Icon: Theme-aware */}
                    <button 
                      className={cn(
                        "ml-1",
                        theme === 'light' ? 'text-gray-500 hover:text-gray-700' : 'text-purple-300/70 hover:text-purple-300'
                      )}
                      title={periodType === 'tax-year' 
                        ? 'UK Tax Year runs from April 6th to April 5th of the following year' 
                        : 'Data shown based on expense dates in the selected period'}
                    >
                      <Info size={14} />
                    </button>
                  </div>
                  {/* Total Label: Theme-aware */}
                  <div className={cn(
                    "text-sm sm:text-base",
                    theme === 'light' ? 'text-gray-700' : 'text-purple-300'
                  )}>
                    Total: <span className={cn("font-bold", theme === 'light' ? 'text-black' : 'text-white')}>£{totalExpenses.toFixed(2)}</span>
                  </div>
                </div>
                {/* Data Points Label: Theme-aware */}
                <div className={cn(
                  "text-xs mt-1",
                  theme === 'light' ? 'text-gray-500' : 'text-purple-300/70'
                )}>
                  {dataForChart.length} data points from {filteredExpenses.length} expense records
                </div>
              </div>

              {/* Chart Component - Removed theme prop */}
              <div className="h-[300px] sm:h-[350px] md:h-[400px]">
                <ExpenseChart
                  data={dataForChart} // Still pass data, though chart ignores it for now
                  chartType={chartType} // Still pass type, though chart ignores it for now
                  // theme={theme} // REMOVED theme prop for testing
                />
              </div>

              {/* Details Section */}
              <div className="mt-6">
                {/* Details Header: Theme-aware */}
                <div
                  className={cn(
                    "flex justify-between items-center cursor-pointer py-2 border-b",
                    theme === 'light' ? 'border-gray-300' : 'border-purple-500/20'
                  )}
                  onClick={() => setShowDetails(!showDetails)}
                >
                  {/* Details Title: Theme-aware */}
                  <span className={cn("text-lg", theme === 'light' ? 'text-black' : 'text-purple-300')}>Expense Details</span>
                  {/* Show/Hide Button: Theme-aware */}
                  <Button
                    variant="outline" // Use outline for better theme adaptation
                    size="sm"
                    className={cn(
                      theme === 'light' 
                        ? 'text-black border-gray-300 hover:bg-gray-100' 
                        : 'text-purple-300 hover:text-purple-100 hover:bg-purple-900/30 border border-purple-300/30'
                    )}
                  >
                    {showDetails ? 'Hide' : 'Show'}
                  </Button>
                </div>

                {/* Details Table */}
                {showDetails && (
                  <div className="mt-4 overflow-x-auto">
                    <table className="w-full table-auto">
                      {/* Table Header: Theme-aware */}
                      <thead className={cn(theme === 'light' ? 'bg-gray-100' : 'bg-[#1F1F2E]')}>
                        <tr>
                          <th className={cn("p-2 text-left", theme === 'light' ? 'text-gray-600' : 'text-purple-300')}>Period</th>
                          <th className={cn("p-2 text-right", theme === 'light' ? 'text-gray-600' : 'text-purple-300')}>Amount</th>
                          <th className={cn("p-2 text-right", theme === 'light' ? 'text-gray-600' : 'text-purple-300')}>% of Total</th>
                        </tr>
                      </thead>
                      <tbody>
                        {dataForChart.map((item, i) => ( // Use dataForChart here
                          // Table Rows: Theme-aware alternating background
                          <tr key={i} className={cn(
                            theme === 'light' 
                              ? (i % 2 === 0 ? 'bg-white' : 'bg-gray-50') 
                              : (i % 2 === 0 ? 'bg-[#2A2A3E]' : 'bg-[#1F1F2E]')
                          )}>
                            {/* Table Cells: Theme-aware text */}
                            <td className={cn("p-2", theme === 'light' ? 'text-black' : 'text-white')}>{item.label}</td>
                            <td className={cn("p-2 text-right", theme === 'light' ? 'text-black' : 'text-white')}>£{item.value.toFixed(2)}</td>
                            <td className={cn("p-2 text-right", theme === 'light' ? 'text-black' : 'text-white')}>
                              {/* Use memoized totalExpenses */}
                              {totalExpenses ? ((item.value / totalExpenses) * 100).toFixed(1) : 0}% 
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            </>
          ) : (
            // No Data Message: Theme-aware
            <div className={cn(
              "p-10 text-center",
              theme === 'light' ? 'text-gray-500' : 'text-gray-400'
            )}>
              {expenses.length === 0 
                ? "No expense data available. Add some expenses to see reports."
                : `No expense data found for ${getPeriodDisplayLabel()}. Try changing the period or filters.`} {/* More specific message */}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Archive Dialog: Theme-aware */}
      <Dialog open={showArchiveDialog} onOpenChange={setShowArchiveDialog}>
        <DialogContent className={cn(theme === 'light' ? 'bg-white' : 'bg-[#2A2A3E] border-purple-500/20')}>
          <DialogHeader>
            {/* Title: Theme-aware */}
            <DialogTitle className={cn(theme === 'light' ? 'text-black' : 'text-purple300')}>Include Archived Expenses</DialogTitle>
            {/* Description: Theme-aware */}
            <DialogDescription className={cn(theme === 'light' ? 'text-gray-600' : 'text-gray-300')}>
              Select a date range to include older expense records in your report. Note that this may increase database usage.
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 my-4">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                {/* Label: Theme-aware */}
                <Label className={cn("mb-1 block", theme === 'light' ? 'text-black' : 'text-purple-300')}>From Date</Label>
                <Popover>
                  <PopoverTrigger asChild>
                    {/* Button: Theme-aware */}
                    <Button 
                      variant="outline" 
                      className={cn(
                        "w-full justify-start text-left font-normal",
                        theme === 'light' 
                          ? 'bg-white border-gray-300 text-black' 
                          : 'bg-[#1F1F2E] border-purple-500/30 text-white'
                      )}
                    >
                      <CalendarIcon className="mr-2 h-4 w-4" />
                      {archiveDateRange.from ? format(archiveDateRange.from, 'PPP') : "Select date"}
                    </Button>
                  </PopoverTrigger>
                  {/* Popover Content: Theme-aware */}
                  <PopoverContent className={cn(
                    "w-auto p-0",
                    theme === 'light' ? 'bg-white border-gray-300' : 'bg-[#2A2A3E] border-purple-500/30'
                  )}>
                    {/* Calendar: Assuming it adapts */}
                    <Calendar
                      mode="single"
                      selected={archiveDateRange.from}
                      onSelect={(date) => setArchiveDateRange(prev => ({ ...prev, from: date }))}
                      disabled={(date) => date > new Date() || (archiveDateRange.to && date > archiveDateRange.to)}
                      initialFocus
                    />
                  </PopoverContent>
                </Popover>
              </div>
              
              <div>
                {/* Label: Theme-aware */}
                <Label className={cn("mb-1 block", theme === 'light' ? 'text-black' : 'text-purple-300')}>To Date</Label>
                 <Popover>
                  <PopoverTrigger asChild>
                    {/* Button: Theme-aware */}
                    <Button 
                      variant="outline" 
                      className={cn(
                        "w-full justify-start text-left font-normal",
                        theme === 'light' 
                          ? 'bg-white border-gray-300 text-black' 
                          : 'bg-[#1F1F2E] border-purple-500/30 text-white'
                      )}
                    >
                      <CalendarIcon className="mr-2 h-4 w-4" />
                      {archiveDateRange.to ? format(archiveDateRange.to, 'PPP') : "Select date"}
                    </Button>
                  </PopoverTrigger>
                  {/* Popover Content: Theme-aware */}
                  <PopoverContent className={cn(
                    "w-auto p-0",
                    theme === 'light' ? 'bg-white border-gray-300' : 'bg-[#2A2A3E] border-purple-500/30'
                  )}>
                    {/* Calendar: Assuming it adapts */}
                    <Calendar
                      mode="single"
                      selected={archiveDateRange.to}
                      onSelect={(date) => setArchiveDateRange(prev => ({ ...prev, to: date }))}
                      disabled={(date) => date > new Date() || (archiveDateRange.from && date < archiveDateRange.from)}
                      initialFocus
                    />
                  </PopoverContent>
                </Popover>
              </div>
            </div>
          </div>
          
          <DialogFooter>
            {/* Cancel Button: Theme-aware */}
            <Button 
              variant="ghost" 
              onClick={() => setShowArchiveDialog(false)}
              className={cn(theme === 'light' ? 'text-black hover:bg-gray-100' : 'text-white hover:bg-purple-900/30')}
            >
              Cancel
            </Button>
            {/* Include Button: Theme-aware */}
            <Button
              onClick={fetchArchiveData}
              disabled={archiveLoading || !archiveDateRange.from || !archiveDateRange.to}
              className={cn(
                'bg-purple-600 text-white hover:bg-purple-700' // Consistent purple
              )}
            >
              {archiveLoading ? 'Loading...' : 'Include in Report'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}; 

export default ExpenseReports;
